<template>
  <div :id="`custom_date_filter`" class="flex flex-row gap-2 items-center">
    <div class="flex flex-col px-3 pt-1 w-56">
      <div
        v-if="!hideHeader"
        class="flex flex-row text-sm items-center pb-2"
        :class="{ border_bottom: !hideHeader }"
      >
        <ThemeImage
          class="size-4 cursor-pointer"
          :icon="imageNames.BACK_ARROW_WHITE"
          alt="back_arrow_white"
          aria-label="Go back"
          @click="goBack"
        />
        <span
          class="proxima_nova text_color font-semibold text-sm px-3 items-center"
        >
          Custom Date
        </span>
      </div>

      <div class="flex flex-col max-h-[244px] gap-2 py-2">
        <span class="secondary_color proxima_nova font-medium text-sm">
          From
        </span>
        <a-date-picker
          v-model:value="startDate"
          :format="dateFormat"
          :disabled-date="disabledStartDate"
          @update:value="handleDateChange"
          :inputReadOnly="true"
          popup-class-name="custom-date-filter-dropdown"
        />
        <span class="secondary_color proxima_nova font-medium text-sm">
          To
        </span>
        <a-date-picker
          v-model:value="endDate"
          :format="dateFormat"
          :disabled-date="disabledEndDate"
          @update:value="handleDateChange"
          :inputReadOnly="true"
          popup-class-name="custom-date-filter-dropdown"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import dayjs from "dayjs";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { dateTimeFormats } from "@/src/constants/dateTimeConstants.js";

const props = defineProps({
  parentFormat: { type: String, default: dateTimeFormats[3] },
  source: { type: String, required: true },
  startDate: { type: [String, Object], default: null },
  endDate: { type: [String, Object], default: null },
  hideHeader: { type: Boolean, default: false },
  allowFutureDates: { type: Boolean, default: false },
});

const emit = defineEmits(["update:customDateChange", "goBack"]);
const startDate = ref(dayjs(props.startDate));
const endDate = ref(dayjs(props.endDate));
const dateFormat = computed(() => props.parentFormat);

const isValidDateRange = (start, end) => {
  const diffInYears = end.diff(start, "year", true);
  return diffInYears < 1;
};

const disabledStartDate = (current) => {
  if (props.allowFutureDates) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    current &&
    current > today.setHours(23, 59, 59, 999) &&
    !["close_date", "due_date", "date"].includes(props.source)
  );
};

const disabledEndDate = (current) => {
  if (props.allowFutureDates) {
    return current && startDate.value && current < startDate.value;
  }
  return (
    current &&
    ((current > new Date().setHours(23, 59, 59, 999) &&
      !["close_date", "due_date", "date"].includes(props.source)) ||
      (startDate.value && current < startDate.value))
  );
};

const formatDate = (date) => {
  if (!date) return null;
  return date.format(props.parentFormat);
};

const handleDateChange = () => {
  if (startDate.value && endDate.value) {
    if (!isValidDateRange(startDate.value, endDate.value)) {
      message.warning("Date range cannot exceed 1 year");
      endDate.value = startDate.value.add(1, "year").subtract(1, "day");
    }
    emitDateRange();
  }
};

const emitDateRange = () => {
  const range = {
    startDate: formatDate(startDate.value),
    endDate: formatDate(endDate.value),
    source: props.source,
  };
  emit("update:customDateChange", range);
};

const goBack = () => {
  emit("goBack", { source: props.source });
};
</script>

<style>
.custom-date-filter-dropdown {
  z-index: 10000 !important;
}
</style>
